/**
 * LeasingList — Phase 6.1 leasing workspace.
 *
 * Three search modes:
 *   - **Smlouvy**: name + č. smlouvy search → `GET /v1/leasing/smlouvy`
 *   - **Nabídky**: name + č. nabídky → `GET /v1/leasing/nabidky`
 *   - **Vyhledat**: VIN/SPZ → `GET /v1/leasing/smlouvy/search`
 *
 * Layout: Shell · AppRail (leasing active) · Crown (breadcrumb · Smlouvy
 * operativního leasingu · CrownMeta · Subtabs) · Toolband (mode-specific search
 * inputs + Nová smlouva CTA) · `<DataTable>` of records ·
 * `<Inspector>` preview swapping on row select.
 *
 * Row tinting (legacy parity): early termination → warn marker,
 * expired by `datum_do` → neutral marker, otherwise active.
 *
 * Each inspector is a read-only preview with a single "Detail" link to the
 * relevant React detail route (`/leasing/$id` for smlouvy and search hits,
 * `/leasing/nabidky/$id` for nabídky). The offer/contract verbs (copy,
 * convert, delete, print, edit) live on those detail pages, not here.
 */

import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { FieldRow, Inspector } from '@/components/Inspector';
import { Icon } from '@/components/Icon';
import { Marker } from '@/components/Marker';
import { SearchInput } from '@/components/SearchInput';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { Subtabs } from '@/components/Subtabs';
import { Toolband } from '@/components/Toolband';
import { formatInt } from '@/lib/format';
import { useModuleAccess } from '@/lib/permissions';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { useBreakpoint } from '@/lib/useBreakpoint';
import {
  useLeasingSearchQuery,
  useNabidkyQuery,
  useSmluvyQuery,
  type LeasingNabidka,
  type LeasingSearchResult,
  type LeasingSmlouva,
} from './api';
import {
  clientLine,
  lifecycleLabel,
  lifecycleTone,
  searchClientLine,
  searchResultLifecycle,
  shortDate,
  smlouvaCode,
  smlouvaLifecycle,
} from './helpers';

export type LeasingTab = 'smlouvy' | 'nabidky' | 'search';

/**
 * Subtab values include two non-view entries: `prehledy` (own route, Phase 6.6)
 * and `vop` (opens the static Všeobecné obchodní podmínky PDF in a new tab,
 * Phase 6.8). Neither ever becomes the active `tab`.
 */
type LeasingNavTab = LeasingTab | 'prehledy' | 'vop';

const TAB_OPTIONS: { value: LeasingNavTab; label: string }[] = [
  { value: 'smlouvy', label: 'Smlouvy' },
  { value: 'nabidky', label: 'Nabídky' },
  { value: 'search', label: 'Vyhledat (VIN / SPZ)' },
  { value: 'prehledy', label: 'Přehledy' },
  { value: 'vop', label: 'Obchodní podmínky' },
];

/** Static legal doc served from the SPA's public dir (`/vop.pdf`). */
const VOP_PDF_URL = `${import.meta.env.BASE_URL}vop.pdf`;

const LIST_COLUMNS = '92px minmax(0,1.4fr) minmax(0,1.4fr) 140px 130px 110px';
const SEARCH_COLUMNS = '92px minmax(0,1fr) minmax(0,1.2fr) minmax(0,1.4fr) 110px 110px';

interface MarkerInfo {
  tone: 'ok' | 'warn' | 'neutral';
  label: string;
}

function listMarker(s: { predcasne_ukonceno: string | null; datum_do: string | null }): MarkerInfo {
  const lc = smlouvaLifecycle(s);
  return { tone: lifecycleTone(lc), label: lifecycleLabel(lc) };
}

export function LeasingList() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/leasing' });
  const tab: LeasingTab = search.tab ?? 'smlouvy';
  const { isPhone } = useBreakpoint();
  const { can } = useModuleAccess();
  const canWrite = can('leasing', 'write');

  const [name, setName] = useState(search.name ?? '');
  const [cislo, setCislo] = useState(search.cislo ?? '');
  const [vin, setVin] = useState(search.vin ?? '');
  const [spz, setSpz] = useState(search.spz ?? '');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const dName = useDebouncedValue(name, 300);
  const dCislo = useDebouncedValue(cislo, 300);
  const dVin = useDebouncedValue(vin, 300);
  const dSpz = useDebouncedValue(spz, 300);

  const setTab = (next: LeasingNavTab) => {
    if (next === 'vop') {
      window.open(VOP_PDF_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    if (next === 'prehledy') {
      navigate({ to: '/leasing/prehledy' });
      return;
    }
    setSelectedId(null);
    navigate({
      to: '/leasing',
      search: { ...search, tab: next === 'smlouvy' ? undefined : next },
      replace: true,
    });
  };

  // ----- queries (only the active tab actually fires) -----

  // limit 0 = unlimited (legacy "Načíst vše"); start at the legacy page of 50.
  const [smluvyLimit, setSmluvyLimit] = useState(50);
  const [nabidkyLimit, setNabidkyLimit] = useState(50);
  useEffect(() => {
    setSmluvyLimit(50);
    setNabidkyLimit(50);
  }, [dName, dCislo]);

  const smluvyQuery = useSmluvyQuery(
    {
      hledanejmeno: dName || undefined,
      cislo_smlouvy: dCislo || undefined,
      limit: String(smluvyLimit),
    },
    { enabled: tab === 'smlouvy' },
  );
  const nabidkyQuery = useNabidkyQuery(
    {
      hledanejmeno: dName || undefined,
      cislo_nabidky: dCislo || undefined,
      limit: String(nabidkyLimit),
    },
    { enabled: tab === 'nabidky' },
  );
  const searchQuery = useLeasingSearchQuery(
    tab === 'search'
      ? {
          vin: dVin || undefined,
          registracni_znacka: dSpz || undefined,
        }
      : {},
  );
  const smluvy = (tab === 'smlouvy' ? smluvyQuery.data?.smlouvy : []) ?? [];
  const nabidky = (tab === 'nabidky' ? nabidkyQuery.data?.nabidky : []) ?? [];
  const searchResults = (tab === 'search' ? searchQuery.data?.results : []) ?? [];

  const activeQuery =
    tab === 'smlouvy'
      ? smluvyQuery
      : tab === 'nabidky'
        ? nabidkyQuery
        : searchQuery;
  const isLoading = activeQuery.isLoading;
  const isError = activeQuery.isError;

  const counts = useMemo(() => {
    if (tab === 'smlouvy') {
      const lifecycles = smluvy.map(smlouvaLifecycle);
      return {
        celkem: smluvy.length,
        aktivni: lifecycles.filter((l) => l === 'active').length,
        ukoncene: lifecycles.filter((l) => l === 'terminated').length,
      };
    }
    if (tab === 'nabidky') {
      return { celkem: nabidky.length, aktivni: nabidky.length, ukoncene: 0 };
    }
    const lifecycles = searchResults.map(searchResultLifecycle);
    return {
      celkem: searchResults.length,
      aktivni: lifecycles.filter((l) => l === 'active').length,
      ukoncene: lifecycles.filter((l) => l === 'terminated').length,
    };
  }, [tab, smluvy, nabidky, searchResults]);

  const selectedSmlouva =
    tab === 'smlouvy'
      ? smluvy.find((s) => s.id_smlouvy === selectedId) ?? smluvy[0] ?? null
      : null;
  const selectedNabidka =
    tab === 'nabidky'
      ? nabidky.find((n) => n.id_smlouvy === selectedId) ?? nabidky[0] ?? null
      : null;
  const selectedSearch =
    tab === 'search'
      ? searchResults.find((r) => r.id_smlouvy === selectedId) ?? searchResults[0] ?? null
      : null;

  const inspector = (() => {
    if (selectedSmlouva) return <SmlouvaInspector smlouva={selectedSmlouva} />;
    if (selectedNabidka) return <NabidkaInspector nabidka={selectedNabidka} />;
    if (selectedSearch) return <SearchInspector result={selectedSearch} />;
    return <EmptyInspector />;
  })();

  const heading: Record<LeasingTab, { eyebrow: string; main: string }> = {
    smlouvy: {
      eyebrow: 'Leasing',
      main: 'Operativní leasing',
    },
    nabidky: {
      eyebrow: 'Leasing',
      main: 'Operativní leasing',
    },
    search: {
      eyebrow: 'Leasing',
      main: 'Vyhledat',
    },
  };

  const newCtaLabel = tab === 'nabidky' ? 'Nová nabídka' : 'Nová smlouva';
  const newCtaKind: 'nabidka' | 'smlouva' = tab === 'nabidky' ? 'nabidka' : 'smlouva';

  return (
    <Shell rail={<AppRail active="leasing" />} inspector={inspector}>
      <Crown>
        <Breadcrumb
          segments={[
            { label: <Link to="/leasing">Leasing</Link> },
            {
              label:
                tab === 'nabidky'
                  ? 'Nabídky'
                  : tab === 'search'
                    ? 'Vyhledat'
                    : 'Smlouvy',
            },
          ]}
        />
        <Crown.Row className="mt-3">
          <DisplayHeading>{heading[tab].main}</DisplayHeading>
          <CrownMeta
            items={[
              { label: 'Celkem', value: formatInt(counts.celkem) },
              { label: tab === 'nabidky' ? 'Otevřené' : 'Aktivní', value: formatInt(counts.aktivni) },
              ...(tab === 'nabidky'
                ? []
                : [{ label: 'Ukončené', value: formatInt(counts.ukoncene) }]),
            ]}
          />
        </Crown.Row>
        <Crown.Row className="mt-3">
          <Subtabs<LeasingNavTab>
            aria-label="Leasing sekce"
            items={TAB_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
              ...(o.value === tab ? { count: counts.celkem } : {}),
            }))}
            value={tab}
            onChange={setTab}
          />
        </Crown.Row>
      </Crown>

      <Toolband>
        {tab === 'search' ? (
          <>
            <SearchInput
              placeholder="VIN…"
              value={vin}
              onChange={(e) => setVin(e.target.value)}
              wrapperClassName="max-w-[260px] flex-1"
              data-testid="leasing-search-vin"
            />
            <SearchInput
              placeholder="SPZ…"
              value={spz}
              onChange={(e) => setSpz(e.target.value)}
              wrapperClassName="max-w-[200px] flex-1"
              data-testid="leasing-search-spz"
            />
          </>
        ) : (
          <>
            <SearchInput
              placeholder="Jméno klienta…"
              value={name}
              onChange={(e) => setName(e.target.value)}
              wrapperClassName="max-w-[300px] flex-1"
              data-testid="leasing-search-name"
            />
            <SearchInput
              placeholder={tab === 'nabidky' ? 'Č. nabídky…' : 'Č. smlouvy…'}
              value={cislo}
              onChange={(e) => setCislo(e.target.value)}
              wrapperClassName="max-w-[200px] flex-1"
              data-testid="leasing-search-cislo"
            />
          </>
        )}
        <Toolband.Spacer />
        {canWrite && (
          <Button
            size="sm"
            variant="primary"
            icon={<Icon name="plus" size={12} />}
            onClick={() => {
              navigate({
                to: '/leasing/wizard',
                search: { kind: newCtaKind, step: 'zakaznik' },
              });
            }}
          >
            {newCtaLabel}
          </Button>
        )}
      </Toolband>

      <div
        data-testid="leasing-page"
        className="flex min-h-0 flex-1 flex-col px-8 pb-6 pt-3 max-sm:px-0 max-sm:pb-4 max-sm:pt-1"
      >
        {tab === 'search' && !dVin && !dSpz ? (
          <EmptyState
            eyebrow="Začněte hledáním"
            title="Zadejte VIN nebo SPZ"
            description="Vyhledávání pracuje napříč všemi firmami — zadejte VIN nebo registrační značku vozidla."
          />
        ) : tab === 'smlouvy' ? (
          <>
          <DataTable<LeasingSmlouva>
            aria-label="Seznam smluv"
            variant="bordered"
            columns={smluvyColumns()}
            data={isLoading ? [] : smluvy}
            getRowId={(s) => String(s.id_smlouvy)}
            selectedId={
              selectedSmlouva ? String(selectedSmlouva.id_smlouvy) : undefined
            }
            onRowClick={(s) =>
              // Phone (14.3): no inspector exists — tapping a card navigates
              // to the detail route.
              isPhone
                ? navigate({
                    to: '/leasing/$id',
                    params: { id: String(s.id_smlouvy) },
                  })
                : setSelectedId(s.id_smlouvy)
            }
            empty={
              <ListEmpty
                isLoading={isLoading}
                isError={isError}
                title="Žádné smlouvy"
              />
            }
            className="min-h-0 flex-1"
          />
            {smluvyLimit !== 0 && smluvy.length >= smluvyLimit && (
              <div className="flex justify-center pt-3">
                <Button variant="secondary" size="sm" onClick={() => setSmluvyLimit(0)}>
                  Načíst vše
                </Button>
              </div>
            )}
          </>
        ) : tab === 'nabidky' ? (
          <>
          <DataTable<LeasingNabidka>
            aria-label="Seznam nabídek"
            variant="bordered"
            columns={nabidkyColumns()}
            data={isLoading ? [] : nabidky}
            getRowId={(n) => String(n.id_smlouvy)}
            selectedId={
              selectedNabidka ? String(selectedNabidka.id_smlouvy) : undefined
            }
            onRowClick={(n) =>
              // Phone (14.3): no inspector exists — tapping a card navigates to
              // the offer detail route (same target as the desktop inspector's
              // "Detail" action).
              isPhone
                ? navigate({
                    to: '/leasing/nabidky/$id',
                    params: { id: String(n.id_smlouvy) },
                  })
                : setSelectedId(n.id_smlouvy)
            }
            empty={
              <ListEmpty
                isLoading={isLoading}
                isError={isError}
                title="Žádné nabídky"
              />
            }
            className="min-h-0 flex-1"
          />
            {nabidkyLimit !== 0 && nabidky.length >= nabidkyLimit && (
              <div className="flex justify-center pt-3">
                <Button variant="secondary" size="sm" onClick={() => setNabidkyLimit(0)}>
                  Načíst vše
                </Button>
              </div>
            )}
          </>
        ) : (
          <DataTable<LeasingSearchResult>
            aria-label="Výsledky vyhledávání"
            variant="bordered"
            columns={searchColumns()}
            data={isLoading ? [] : searchResults}
            getRowId={(r) => String(r.id_smlouvy)}
            selectedId={
              selectedSearch ? String(selectedSearch.id_smlouvy) : undefined
            }
            onRowClick={(r) =>
              // Phone (14.3): no inspector exists — tapping a card navigates
              // to the detail route (same target as the desktop inspector's
              // "Detail" action, including its access-scope caveat).
              isPhone
                ? navigate({
                    to: '/leasing/$id',
                    params: { id: String(r.id_smlouvy) },
                  })
                : setSelectedId(r.id_smlouvy)
            }
            empty={
              <ListEmpty
                isLoading={isLoading}
                isError={isError}
                title="Žádné výsledky"
              />
            }
            className="min-h-0 flex-1"
          />
        )}
      </div>
    </Shell>
  );
}

// ============================================================================
// Columns — smlouvy
// ============================================================================

function smluvyColumns(): DataTableColumn<LeasingSmlouva>[] {
  const w = LIST_COLUMNS.split(' ');
  return [
    {
      id: 'cislo',
      priority: 'meta',
      header: '№',
      width: w[0],
      cell: (s) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink">
          {smlouvaCode(s.id_smlouvy)}
        </span>
      ),
    },
    {
      id: 'klient',
      priority: 'primary',
      header: 'Klient',
      width: w[1],
      cell: (s) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-semibold tracking-[-0.01em] text-ink">
            {clientLine(s)}
          </span>
          {s.jmeno_uzivatele && (
            <span className="truncate text-[11px] text-ink-3">
              {s.jmeno_uzivatele}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'vozidlo',
      priority: 'secondary',
      header: 'Vozidlo',
      width: w[2],
      cell: (s) => (
        <span className="truncate text-[12px] text-ink-2">
          {s.typ_vozu || '—'}
        </span>
      ),
    },
    {
      id: 'cena',
      priority: 'meta',
      header: 'Cena celkem',
      width: w[3],
      align: 'right',
      cell: (s) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink">
          {formatInt(s.cena_celkem)}{' '}
          <span className="text-ink-3">Kč</span>
        </span>
      ),
    },
    {
      id: 'splatka',
      priority: 'meta',
      header: 'Splátka',
      width: w[4],
      align: 'right',
      cell: (s) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink-2">
          {formatInt(s.splatka)}{' '}
          <span className="text-ink-3">Kč</span>
        </span>
      ),
    },
    {
      id: 'stav',
      priority: 'meta',
      header: 'Stav',
      width: w[5],
      cell: (s) => {
        const m = listMarker(s);
        return <Marker tone={m.tone}>{m.label}</Marker>;
      },
    },
  ];
}

// ============================================================================
// Columns — nabidky
// ============================================================================

function nabidkyColumns(): DataTableColumn<LeasingNabidka>[] {
  const w = LIST_COLUMNS.split(' ');
  return [
    {
      id: 'cislo',
      priority: 'meta',
      header: '№',
      width: w[0],
      cell: (n) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink">
          {smlouvaCode(n.id_smlouvy)}
        </span>
      ),
    },
    {
      id: 'klient',
      priority: 'primary',
      header: 'Klient',
      width: w[1],
      cell: (n) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-semibold tracking-[-0.01em] text-ink">
            {clientLine(n)}
          </span>
          {n.jmeno_uzivatele && (
            <span className="truncate text-[11px] text-ink-3">
              {n.jmeno_uzivatele}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'vozidlo',
      priority: 'secondary',
      header: 'Vozidlo',
      width: w[2],
      cell: (n) => (
        <span className="truncate text-[12px] text-ink-2">
          {n.typ_vozu || '—'}
        </span>
      ),
    },
    {
      id: 'cena',
      priority: 'meta',
      header: 'Cena celkem',
      width: w[3],
      align: 'right',
      cell: (n) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink">
          {formatInt(n.cena_celkem)}{' '}
          <span className="text-ink-3">Kč</span>
        </span>
      ),
    },
    {
      id: 'splatka',
      priority: 'meta',
      header: 'Splátka',
      width: w[4],
      align: 'right',
      cell: (n) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink-2">
          {formatInt(n.splatka)}{' '}
          <span className="text-ink-3">Kč</span>
        </span>
      ),
    },
    {
      id: 'stav',
      priority: 'meta',
      header: 'Stav',
      width: w[5],
      cell: () => <Marker tone="ok">Otevřená</Marker>,
    },
  ];
}

// ============================================================================
// Columns — search results
// ============================================================================

function searchColumns(): DataTableColumn<LeasingSearchResult>[] {
  const w = SEARCH_COLUMNS.split(' ');
  return [
    {
      id: 'cislo',
      priority: 'meta',
      header: '№',
      width: w[0],
      cell: (s) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink">
          {smlouvaCode(s.id_smlouvy)}
        </span>
      ),
    },
    {
      id: 'klient',
      priority: 'primary',
      header: 'Klient',
      width: w[1],
      cell: (s) => (
        <span className="truncate font-semibold tracking-[-0.01em] text-ink">
          {searchClientLine(s)}
        </span>
      ),
    },
    {
      id: 'poskytovatel',
      priority: 'secondary',
      header: 'Poskytovatel',
      width: w[2],
      cell: (s) => (
        <span className="truncate text-[12px] text-ink-2">
          {s.nazev_firmy || '—'}
        </span>
      ),
    },
    {
      id: 'vozidlo',
      priority: 'secondary',
      header: 'Vozidlo',
      width: w[3],
      cell: (s) => (
        <span className="truncate text-[12px] text-ink-2">
          {s.typ_vozu || '—'}
        </span>
      ),
    },
    {
      id: 'spz',
      priority: 'meta',
      header: 'SPZ',
      width: w[4],
      cell: (s) => (
        <span className="font-mono text-[11px] tracking-[-0.02em] text-ink-2">
          {s.registracni_znacka || '—'}
        </span>
      ),
    },
    {
      id: 'stav',
      priority: 'meta',
      header: 'Stav',
      width: w[5],
      cell: (s) => {
        const m = listMarker(s);
        return <Marker tone={m.tone}>{m.label}</Marker>;
      },
    },
  ];
}

// ============================================================================
// Inspectors
// ============================================================================

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-px p-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function ListEmpty({
  isLoading,
  isError,
  title,
}: {
  isLoading: boolean;
  isError: boolean;
  title: string;
}) {
  if (isLoading) return <ListSkeleton />;
  if (isError) {
    return (
      <EmptyState
        eyebrow="Chyba"
        title="Data se nepodařilo načíst"
        description="Zkuste obnovit stránku. Pokud problém trvá, kontaktujte správce."
      />
    );
  }
  return (
    <EmptyState
      eyebrow="Prázdné"
      title={title}
      description="Zkuste rozšířit hledání nebo přidejte novou položku."
    />
  );
}

function EmptyInspector() {
  return (
    <Inspector>
      <div className="grid h-full place-items-center px-6 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
        Vyberte záznam
      </div>
    </Inspector>
  );
}

function SmlouvaInspector({ smlouva }: { smlouva: LeasingSmlouva }) {
  const navigate = useNavigate();
  const m = listMarker(smlouva);
  return (
    <Inspector className="vt-detail-card">
      <Inspector.Head
        eyebrow={
          <>
            — <b>Smlouva № {smlouvaCode(smlouva.id_smlouvy)}</b>
          </>
        }
        title={clientLine(smlouva)}
        sub={
          <span className="inline-flex items-center gap-2">
            <Marker tone={m.tone}>{m.label}</Marker>
          </span>
        }
      />
      <Inspector.Body>
        <Inspector.Section heading="— Vozidlo">
          <FieldRow k="Typ" v={smlouva.typ_vozu || '—'} />
        </Inspector.Section>
        <Inspector.Section heading="— Finance">
          <FieldRow k="Cena celkem" v={`${formatInt(smlouva.cena_celkem)} Kč`} mono />
          <FieldRow k="Splátka" v={`${formatInt(smlouva.splatka)} Kč`} mono />
        </Inspector.Section>
        <Inspector.Section heading="— Termíny">
          <FieldRow k="Datum do" v={shortDate(smlouva.datum_do) || '—'} mono />
          <FieldRow
            k="Předčasně ukončeno"
            v={shortDate(smlouva.predcasne_ukonceno) || '—'}
            mono
          />
        </Inspector.Section>
        {smlouva.jmeno_uzivatele && (
          <Inspector.Section heading="— Vytvořil">
            <FieldRow k="Uživatel" v={smlouva.jmeno_uzivatele} />
          </Inspector.Section>
        )}
      </Inspector.Body>
      <Inspector.Foot>
        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="external" size={12} />}
          onClick={() =>
            navigate({
              to: '/leasing/$id',
              params: { id: String(smlouva.id_smlouvy) },
            })
          }
        >
          Detail
        </Button>
      </Inspector.Foot>
    </Inspector>
  );
}

/**
 * Read-only preview for a leasing offer. The offer's verbs (duplicate ·
 * convert · delete · print · edit) live on the detail page
 * ([LeasingNabidkaDetail.tsx]) — the inspector only previews and links there,
 * matching the SmlouvaInspector and the vozidla NabidkaInspector.
 */
function NabidkaInspector({ nabidka }: { nabidka: LeasingNabidka }) {
  const navigate = useNavigate();
  return (
    <Inspector className="vt-detail-card">
      <Inspector.Head
        eyebrow={
          <>
            — <b>Nabídka № {smlouvaCode(nabidka.id_smlouvy)}</b>
          </>
        }
        title={clientLine(nabidka)}
        sub={<Marker tone="ok">Otevřená</Marker>}
      />
      <Inspector.Body>
        <Inspector.Section heading="— Vozidlo">
          <FieldRow k="Typ" v={nabidka.typ_vozu || '—'} />
        </Inspector.Section>
        <Inspector.Section heading="— Finance">
          <FieldRow k="Cena celkem" v={`${formatInt(nabidka.cena_celkem)} Kč`} mono />
          <FieldRow k="Splátka" v={`${formatInt(nabidka.splatka)} Kč`} mono />
        </Inspector.Section>
        {nabidka.jmeno_uzivatele && (
          <Inspector.Section heading="— Vytvořil">
            <FieldRow k="Uživatel" v={nabidka.jmeno_uzivatele} />
          </Inspector.Section>
        )}
      </Inspector.Body>
      <Inspector.Foot>
        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="external" size={12} />}
          onClick={() =>
            navigate({
              to: '/leasing/nabidky/$id',
              params: { id: String(nabidka.id_smlouvy) },
            })
          }
        >
          Detail
        </Button>
      </Inspector.Foot>
    </Inspector>
  );
}

function SearchInspector({ result }: { result: LeasingSearchResult }) {
  const navigate = useNavigate();
  const m = listMarker(result);
  return (
    <Inspector className="vt-detail-card">
      <Inspector.Head
        eyebrow={
          <>
            — <b>Smlouva № {smlouvaCode(result.id_smlouvy)}</b>
          </>
        }
        title={searchClientLine(result)}
        sub={<Marker tone={m.tone}>{m.label}</Marker>}
      />
      <Inspector.Body>
        <Inspector.Section heading="— Vozidlo">
          <FieldRow k="Typ" v={result.typ_vozu || '—'} />
          <FieldRow k="VIN" v={result.vin || '—'} mono />
          <FieldRow k="SPZ" v={result.registracni_znacka || '—'} mono />
        </Inspector.Section>
        <Inspector.Section heading="— Termíny">
          <FieldRow k="Datum do" v={shortDate(result.datum_do) || '—'} mono />
          <FieldRow
            k="Předčasně ukončeno"
            v={shortDate(result.predcasne_ukonceno) || '—'}
            mono
          />
        </Inspector.Section>
        {result.nazev_firmy && (
          <Inspector.Section heading="— Poskytovatel">
            <FieldRow k="Firma" v={result.nazev_firmy} />
          </Inspector.Section>
        )}
      </Inspector.Body>
      <Inspector.Foot>
        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="external" size={12} />}
          onClick={() => {
            // Cross-firma search may surface a contract owned by another
            // company — the React detail route filters by company-id, so fall
            // back to the legacy detail page when the row's `id_firmy`
            // doesn't match the active session. We can't see the active
            // company id from here, so always navigate to the React route;
            // it'll show the not-found state if the user lacks access.
            navigate({
              to: '/leasing/$id',
              params: { id: String(result.id_smlouvy) },
            });
          }}
        >
          Detail
        </Button>
      </Inspector.Foot>
    </Inspector>
  );
}

